// Comprehensive audit trail system for tracking changes
export interface AuditEvent {
  id: string;
  entityType: 'po' | 'invoice' | 'payment' | 'supplier' | 'customer' | 'asset';
  entityId: string;
  action: 'create' | 'update' | 'delete' | 'approve' | 'reject' | 'status_change' | 'bulk_update';
  userId: string;
  timestamp: string;
  changes?: FieldChange[];
  metadata?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
}

export interface FieldChange {
  field: string;
  oldValue: any;
  newValue: any;
  changeReason?: string;
}

export interface ApprovalHistory {
  id: string;
  entityType: string;
  entityId: string;
  approverLevel: number;
  approverUserId: string;
  action: 'approved' | 'rejected' | 'requested_changes';
  comments?: string;
  timestamp: string;
  nextApprover?: string;
}

export interface DocumentVersion {
  id: string;
  documentId: string;
  version: number;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  uploadedBy: string;
  uploadedAt: string;
  checksum: string;
  isActive: boolean;
}

class AuditTrailManager {
  private events: AuditEvent[] = [];
  private approvals: ApprovalHistory[] = [];
  private documentVersions: DocumentVersion[] = [];

  /**
   * Log an audit event
   */
  logEvent(event: Omit<AuditEvent, 'id' | 'timestamp'>): AuditEvent {
    const auditEvent: AuditEvent = {
      ...event,
      id: `audit_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      timestamp: new Date().toISOString(),
    };

    this.events.unshift(auditEvent);
    
    // Keep only last 10000 events in memory (in real app, this would go to database)
    if (this.events.length > 10000) {
      this.events = this.events.slice(0, 10000);
    }

    return auditEvent;
  }

  /**
   * Log field-level changes
   */
  logFieldChanges(
    entityType: AuditEvent['entityType'],
    entityId: string,
    oldData: any,
    newData: any,
    userId: string,
    changeReasons?: Record<string, string>
  ): AuditEvent {
    const changes: FieldChange[] = [];

    const processObject = (obj1: any, obj2: any, prefix = '') => {
      for (const key in obj2) {
        if (obj2.hasOwnProperty(key)) {
          const fieldPath = prefix ? `${prefix}.${key}` : key;
          
          if (typeof obj2[key] === 'object' && obj2[key] !== null && !Array.isArray(obj2[key])) {
            processObject(obj1[key] || {}, obj2[key], fieldPath);
          } else if (obj1[key] !== obj2[key]) {
            changes.push({
              field: fieldPath,
              oldValue: obj1[key],
              newValue: obj2[key],
              changeReason: changeReasons?.[fieldPath]
            });
          }
        }
      }
    };

    processObject(oldData, newData);

    return this.logEvent({
      entityType,
      entityId,
      action: 'update',
      userId,
      changes,
    });
  }

  /**
   * Log approval workflow events
   */
  logApproval(approval: Omit<ApprovalHistory, 'id' | 'timestamp'>): ApprovalHistory {
    const approvalEvent: ApprovalHistory = {
      ...approval,
      id: `approval_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      timestamp: new Date().toISOString(),
    };

    this.approvals.unshift(approvalEvent);
    return approvalEvent;
  }

  /**
   * Version document uploads
   */
  versionDocument(document: Omit<DocumentVersion, 'id' | 'version' | 'uploadedAt' | 'isActive'>): DocumentVersion {
    // Deactivate previous versions
    this.documentVersions
      .filter(d => d.documentId === document.documentId)
      .forEach(d => d.isActive = false);

    // Get next version number
    const existingVersions = this.documentVersions.filter(d => d.documentId === document.documentId);
    const nextVersion = existingVersions.length > 0 ? Math.max(...existingVersions.map(d => d.version)) + 1 : 1;

    const documentVersion: DocumentVersion = {
      ...document,
      id: `doc_v_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      version: nextVersion,
      uploadedAt: new Date().toISOString(),
      isActive: true,
    };

    this.documentVersions.unshift(documentVersion);
    return documentVersion;
  }

  /**
   * Get audit trail for specific entity
   */
  getEntityAuditTrail(entityType: AuditEvent['entityType'], entityId: string): AuditEvent[] {
    return this.events.filter(e => e.entityType === entityType && e.entityId === entityId);
  }

  /**
   * Get approval history for entity
   */
  getApprovalHistory(entityType: string, entityId: string): ApprovalHistory[] {
    return this.approvals.filter(a => a.entityType === entityType && a.entityId === entityId);
  }

  /**
   * Get document versions
   */
  getDocumentVersions(documentId: string): DocumentVersion[] {
    return this.documentVersions
      .filter(d => d.documentId === documentId)
      .sort((a, b) => b.version - a.version);
  }

  /**
   * Get recent activity across all entities
   */
  getRecentActivity(limit: number = 50): AuditEvent[] {
    return this.events.slice(0, limit);
  }

  /**
   * Search audit trail
   */
  searchAuditTrail(filters: {
    entityType?: AuditEvent['entityType'];
    action?: AuditEvent['action'];
    userId?: string;
    startDate?: string;
    endDate?: string;
    entityId?: string;
  }): AuditEvent[] {
    return this.events.filter(event => {
      if (filters.entityType && event.entityType !== filters.entityType) return false;
      if (filters.action && event.action !== filters.action) return false;
      if (filters.userId && event.userId !== filters.userId) return false;
      if (filters.entityId && event.entityId !== filters.entityId) return false;
      if (filters.startDate && event.timestamp < filters.startDate) return false;
      if (filters.endDate && event.timestamp > filters.endDate) return false;
      return true;
    });
  }

  /**
   * Export audit trail to CSV
   */
  exportToCSV(events: AuditEvent[] = this.events): string {
    const headers = ['Timestamp', 'Entity Type', 'Entity ID', 'Action', 'User ID', 'Changes', 'Metadata'];
    const rows = events.map(event => [
      event.timestamp,
      event.entityType,
      event.entityId,
      event.action,
      event.userId,
      event.changes ? JSON.stringify(event.changes) : '',
      event.metadata ? JSON.stringify(event.metadata) : ''
    ]);

    return [headers, ...rows].map(row => row.map(cell => `\"${cell}\"`).join(',')).join('\n');
  }
}

// Singleton instance
export const auditTrail = new AuditTrailManager();

// Helper function to create consistent user context
export function getCurrentUserContext(): { userId: string; ipAddress?: string; userAgent?: string } {
  return {
    userId: 'current_user', // In real app, get from auth context
    ipAddress: 'unknown', // In real app, get from request
    userAgent: navigator?.userAgent
  };
}
